<template>
  <div class="sales">
    <div class="actions flex flex-col md:flex-row justify-between">
      <div class="action-left mb-2 md:mb-0 md:mr-4">
        <h2 class="text-xl md:text-left">Sales Log</h2>
      </div>

      <div class="search flex flex-wrap items-center md:ml-4">
        <button class="btn btn-main" @click="handleNewEntry">New Entry</button>
        <div class="search-input-wrap">
          <input
            type="text"
            placeholder="Search"
            v-model="search"
            class="input-search"
            @keyup="triggerSearchChange"
            @keyup.esc="clearSearch"
          />
          <button
            v-if="search"
            type="button"
            class="search-clear"
            title="Clear search"
            @click="clearSearch"
          >
            ✕
          </button>
        </div>
      </div>
    </div>

    <div class="table-controls flex items-center justify-between my-2">
      <span class="text-sm text-gray-500">
        <template v-if="loading">Loading…</template>
        <template v-else-if="loadingMore">
          Searching {{ sales.length }} records — still loading older…
        </template>
        <template v-else-if="loadError && !sales.length">
          Couldn't load records
        </template>
        <template v-else-if="loadError">
          Searching {{ sales.length }} records — couldn't load the rest
        </template>
        <template v-else-if="capped">
          Searching most recent {{ sales.length }} records (load limit)
        </template>
        <template v-else-if="!fullLoadRequested && !allLoaded">
          Showing most recent {{ sales.length }} records — search to include older
        </template>
        <template v-else>Searching all {{ sales.length }} records</template>
      </span>
      <button
        v-if="(capped || loadError || (!fullLoadRequested && !allLoaded)) && !loadingMore"
        class="btn btn-secondary"
        @click="loadMore"
      >
        {{ loadError ? "Retry" : !fullLoadRequested ? "Load all" : "Load more" }}
      </button>
    </div>

    <SalesEditor
      v-if="showEditor"
      :editId="editId"
      :editData="editData"
      @overlayClick="handleOverlayClick"
      @recordCreated="handleRecordCreated"
      @recordUpdated="handleRecordUpdated"
      @recordDeleted="handleRecordDeleted"
    ></SalesEditor>
    <div class="grid-wrap" style="height: calc(100vh - 210px)">
      <ag-grid-vue
        style="width: 100%; height: 100%"
        class="ag-theme-alpine ag-theme-capitol"
        :getRowId="(params) => params.data.id"
        :columnDefs="columnDefs"
        :rowData="sales"
        :defaultColDef="defaultColDef"
        :getContextMenuItems="getContextMenuItems"
        :suppressColumnMoveAnimation="true"
        :pagination="true"
        :paginationPageSize="100"
        :enableBrowserTooltips="true"
        :cacheQuickFilter="true"
        overlayLoadingTemplate="<span class='ag-overlay-loading-center'>Loading sales…</span>"
        :statusBar="{
          statusPanels: [
            {
              statusPanel: 'agTotalAndFilteredRowCountComponent',
              align: 'left',
            },
          ],
        }"
        @grid-ready="onGridReady"
      >
      </ag-grid-vue>
    </div>
  </div>
</template>

<script>
import "../../lib/ag-grid-enterprise";
import { AgGridVue } from "ag-grid-vue";
import { db } from "../database/db.js";
import SalesEditor from "@/components/SalesEditor.vue";
import { dateColumn } from "@/utils/columns";

// Number of records fetched per Firestore request. The first page renders
// immediately. The rest of the collection is NOT fetched on mount — it streams
// in via loadRemaining() only once something asks for it (a search term, or the
// "Load all" button). See ensureFullLoad().
const PAGE_SIZE = 500;

// Ceiling on rows held in memory. Search is client-side (ag-grid quick filter),
// so every row must be loaded to be findable — but an unbounded load would
// punish phones and run up Firestore reads. If the collection turns out to be
// comfortably under this, raise it; the UI says so honestly when it's hit.
const MAX_ROWS = 20000;

// Rows to buffer before pushing them into the grid. Every assignment to `sales`
// costs ag-grid an immutable diff plus a re-sort of the *whole* array, so
// flushing each 500-row page made the tail of a large load progressively
// slower. Batching trades a coarser progress counter for a much faster load.
const FLUSH_EVERY = 2500;

// A blocked Firestore transport hangs instead of rejecting, so `await get()`
// can never settle and the grid keeps its "Loading sales…" overlay with no
// error and no Retry. Bound the initial fetch so it always resolves into
// either rows or a recoverable error state.
const FETCH_TIMEOUT_MS = 15000;

const defaultColDef = {
  flex: 1,
  editable: false,
  sortable: true,
  resizable: true,
  wrapText: false,
  wrapHeaderText: true,
  width: 140,
  enableCellChangeFlash: true,
  // Show the full cell text on hover, since fixed row height truncates it.
  tooltipValueGetter: (params) => params.value,
};

export default {
  name: "App",
  data() {
    return {
      search: "",
      sales: [],
      columnDefs: null,
      defaultColDef,
      gridApi: null,
      columnApi: null,
      showEditor: false,
      editId: null,
      editData: null,
      // pagination state
      lastDoc: null, // last Firestore DocumentSnapshot of the loaded window (cursor for the next page)
      loading: false, // initial page in flight
      loadingMore: false, // background load of older pages in flight
      allLoaded: false, // reached the end of the collection
      capped: false, // stopped early at rowLimit, so search coverage is partial
      loadError: false, // background load aborted on error, not on the cap
      rowLimit: MAX_ROWS, // current ceiling; "Load more" raises it another MAX_ROWS
      isDestroyed: false, // set on teardown to abort the background load
      fullLoadRequested: false, // whether anything has asked for the whole collection yet
    };
  },
  components: {
    AgGridVue,
    SalesEditor,
  },
  created() {
    // Short debounce only to keep typing smooth over a large in-memory set;
    // this is a local filter, so there's no network call to throttle.
    this.triggerSearchChange = this.debounce(() => {
      this.applyQuickFilter();
    }, 150);
    this.onWindowResize = this.debounce(() => {
      this.applyResponsiveDateColumn();
    }, 200);
    window.addEventListener("resize", this.onWindowResize);

    this.fetchInitial();
  },
  beforeDestroy() {
    // Stops loadRemaining() from continuing to page through Firestore (and
    // writing to a torn-down component) after the user navigates away.
    this.isDestroyed = true;
    window.removeEventListener("resize", this.onWindowResize);
  },
  methods: {
    salesCollection() {
      return db.collection("sales");
    },
    docToRow(doc) {
      return { id: doc.id, ...doc.data() };
    },
    // Reject a promise that takes too long, so a hung request surfaces as an
    // error the UI can recover from rather than an overlay that never clears.
    withTimeout(promise, ms, label) {
      let timer;
      const timeout = new Promise((_, reject) => {
        timer = setTimeout(
          () => reject(new Error(`${label} timed out after ${ms}ms`)),
          ms
        );
      });
      return Promise.race([promise, timeout]).then(
        (value) => {
          clearTimeout(timer);
          return value;
        },
        (err) => {
          clearTimeout(timer);
          throw err;
        }
      );
    },
    clearSearch() {
      this.search = "";
      this.applyQuickFilter();
    },
    // Client-side search across every column, matching the Dashboard.
    // Substring, case-insensitive, no field selection. Typing is what pulls
    // the rest of the collection down — see ensureFullLoad.
    applyQuickFilter() {
      if (this.search) this.ensureFullLoad();
      if (this.gridApi) this.gridApi.setQuickFilter(this.search);
    },
    // Pull the whole collection so the quick filter can match against it.
    //
    // This costs one Firestore read per document — up to MAX_ROWS every time
    // the view mounts — and it used to run unconditionally from fetchInitial().
    // Opening the Sales Log to glance at recent entries therefore read the
    // entire collection, which is what drove reads to 200k–350k/day against
    // ~1.5k writes. Most visits never search, so the full load now waits until
    // something actually needs it: a search term, or an explicit "Load more".
    // Idempotent — loadRemaining() is also guarded by loadingMore/allLoaded.
    ensureFullLoad() {
      if (this.fullLoadRequested) return;
      this.fullLoadRequested = true;
      this.loadRemaining();
    },
    // Toggle ag-grid's loading overlay for full-dataset fetches (initial/search).
    setGridLoading(isLoading) {
      if (!this.gridApi) return;
      if (isLoading) {
        this.gridApi.showLoadingOverlay();
      } else if (this.sales.length) {
        this.gridApi.hideOverlay();
      } else {
        this.gridApi.showNoRowsOverlay();
      }
    },
    // Fetch one page of records older than the current cursor.
    async fetchPage() {
      let query = this.salesCollection().orderBy("sales_date", "desc");
      if (this.lastDoc) query = query.startAfter(this.lastDoc);
      const snap = await query.limit(PAGE_SIZE).get();
      if (snap.docs.length) this.lastDoc = snap.docs[snap.docs.length - 1];
      if (snap.size < PAGE_SIZE) this.allLoaded = true;
      return snap.docs.map(this.docToRow);
    },
    // Initial load: render the most recent PAGE_SIZE records as fast as
    // possible, then pull the rest in behind them so search covers everything.
    async fetchInitial() {
      this.loading = true;
      this.setGridLoading(true);
      this.allLoaded = false;
      this.capped = false;
      this.loadError = false;
      this.rowLimit = MAX_ROWS;
      this.lastDoc = null;
      try {
        this.sales = await this.withTimeout(
          this.fetchPage(),
          FETCH_TIMEOUT_MS,
          "Initial sales load"
        );
      } catch (e) {
        // Surface a Retry affordance rather than stranding an empty grid.
        this.loadError = true;
        this.handleError(e);
        return;
      } finally {
        this.loading = false;
        this.setGridLoading(false);
      }
      // Deliberately no loadRemaining() here — see ensureFullLoad().
    },
    // Background load: page through the rest of the collection, appending as
    // each page arrives so the grid stays usable and searchable throughout.
    async loadRemaining() {
      if (this.loadingMore || this.allLoaded) return;
      this.loadingMore = true;
      this.capped = false;
      this.loadError = false;
      let buffer = [];
      // Reassign so ag-grid runs an immutable keyed diff (getRowId is set). The
      // active quick filter is retained in grid state and applied to the new
      // rows automatically, so search coverage grows with each flush.
      const flush = () => {
        if (!buffer.length) return;
        this.sales = this.sales.concat(buffer);
        buffer = [];
      };
      try {
        while (!this.allLoaded && !this.isDestroyed) {
          if (this.sales.length + buffer.length >= this.rowLimit) {
            this.capped = true;
            break;
          }
          const older = await this.fetchPage();
          if (this.isDestroyed) return; // drop the buffer; the view is gone
          if (!older.length) break;
          buffer = buffer.concat(older);
          if (buffer.length >= FLUSH_EVERY) flush();
        }
        flush();
      } catch (e) {
        // Partial data is still useful: keep what loaded and let the user retry.
        flush();
        this.loadError = true;
        this.handleError(e);
      } finally {
        this.loadingMore = false;
      }
    },
    // Raise the ceiling and resume. Without bumping rowLimit, loadRemaining()
    // would re-enter, see the cap already met, and return without fetching.
    loadMore() {
      if (!this.allLoaded) this.rowLimit = this.sales.length + MAX_ROWS;
      this.fullLoadRequested = true; // an explicit ask counts as one
      this.loadRemaining();
    },
    handleError(e) {
      if (e && e.message && e.message.includes("insufficient permissions")) {
        this.$toasted.show("Insufficient Permissions", {
          type: "error",
          theme: "bubble",
          position: "bottom-center",
          duration: 3000,
        });
      } else {
        console.error(e);
        this.$toasted.show("Error loading records", {
          type: "error",
          theme: "bubble",
          position: "bottom-center",
          duration: 3000,
        });
      }
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;

      // The first fetch may still be running when the grid becomes ready.
      if (this.loading) params.api.showLoadingOverlay();

      this.gridColumnApi.applyColumnState({
        state: [{ colId: "sales_date", sort: "desc" }],
        defaultState: { sort: null },
      });
      setTimeout(() => {
        params.api.sizeColumnsToFit();
        this.applyResponsiveDateColumn();
      }, 50);
    },
    // Narrow the date column (and recompute its compact/full text) by viewport.
    applyResponsiveDateColumn() {
      if (!this.gridColumnApi) return;
      const narrow = window.innerWidth < 768;
      this.gridColumnApi.setColumnWidth("sales_date", narrow ? 92 : 122);
      if (this.gridApi) {
        this.gridApi.refreshCells({ columns: ["sales_date"], force: true });
      }
    },
    handleNewEntry() {
      this.editId = null;
      this.editData = null;
      this.showEditor = true;
    },
    handleOverlayClick() {
      this.showEditor = false;
    },
    // With the realtime listener gone, keep the local grid data in sync manually.
    async handleRecordCreated(id) {
      this.showEditor = false;
      try {
        const doc = await this.salesCollection().doc(id).get();
        if (doc.exists) this.sales = [this.docToRow(doc), ...this.sales];
      } catch (e) {
        this.handleError(e);
      }
      this.$nextTick(() => {
        const rowNode = this.gridApi.getRowNode(id);
        if (rowNode) this.gridApi.flashCells({ rowNodes: [rowNode] });
        this.gridApi.sizeColumnsToFit();
      });
    },
    async handleRecordUpdated() {
      this.showEditor = false;
      const id = this.editId;
      if (!id) return;
      try {
        const doc = await this.salesCollection().doc(id).get();
        if (doc.exists) {
          const updated = this.docToRow(doc);
          this.sales = this.sales.map((r) => (r.id === id ? updated : r));
        }
      } catch (e) {
        this.handleError(e);
      }
    },
    handleRecordDeleted() {
      this.showEditor = false;
      const id = this.editId;
      if (id) this.sales = this.sales.filter((r) => r.id !== id);
    },
    debounce(func, wait) {
      let timeout;
      return function(...args) {
        const later = () => {
          clearTimeout(timeout);
          func.apply(this, args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },
    getContextMenuItems(params) {
      var result = [
        {
          name: "Edit Record",
          action: () => {
            this.showEditor = true;
            this.editId = params.node.id;
            this.editData = params.node.data;
          },
        },
        {
          name: "Clone Record",
          action: () => {
            this.showEditor = true;
            this.editId = null;
            this.editData = params.node.data;
          },
        },
      ];
      return result;
    },
  },
  beforeMount() {
    this.columnDefs = [
      dateColumn("sales_date", "Sales Date"),
      { field: "load_num", headerName: "Load Number", minWidth: 80 },
      { field: "type", width: 120, minWidth: 120 },
      {
        field: "num_of_head",
        headerName: "Number of Head",
        minWidth: 80,
        type: "rightAligned",
      },
      { field: "customer", minWidth: 80 },
      { field: "destination", minWidth: 80 },
      { field: "truck_num", headerName: "Truck Number", minWidth: 80 },
      { field: "pen_num", headerName: "Pen Number", minWidth: 80 },
      {
        field: "scale_weight",
        headerName: "Scale Weight",
        width: 140,
        minWidth: 140,
        type: "rightAligned",
      },
      { field: "lot_num", headerName: "Lot Number", minWidth: 80 },
      { field: "pasture", minWidth: 80 },
      {
        field: "time_of_delivery",
        headerName: "Time Of Delivery",
        minWidth: 80,
      },
      { field: "invoice", minWidth: 80 },
      { field: "remarks", minWidth: 80 },
      {
        field: "off_truck_weight",
        headerName: "Off Truck Wt",
        width: 120,
        minWidth: 120,
        type: "rightAligned",
      },
      { field: "time_unloaded", headerName: "Time Unloaded", minWidth: 80 },
      { field: "time_loaded", headerName: "Time Loaded", minWidth: 80 },
      { field: "salesman", minWidth: 80 },
      { field: "freight", minWidth: 80, type: "rightAligned" },
      { field: "rate", width: 80, minWidth: 80, type: "rightAligned" },
      { field: "miles", width: 80, minWidth: 80, type: "rightAligned" },
      { field: "bill_of_lading", headerName: "Bill of Lading", minWidth: 80 },
    ];
  },
};
</script>

<style lang="scss">
@import "../assets/styles/main.scss";

.search-input-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;

  .input-search {
    // room for the clear button
    padding-right: 1.75rem;
  }

  .search-clear {
    position: absolute;
    right: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    line-height: 1;
    color: #9ca3af;
    cursor: pointer;

    &:hover {
      color: #4b5563;
    }
  }
}

// Action bar: consistent spacing, and wrap gracefully on narrow screens.
.sales .search {
  gap: 0.5rem;
}
@media (max-width: 767px) {
  .sales .search {
    width: 100%;
  }
  // Let the search field grow to fill its row and wrap below the
  // New Entry button / field selector instead of overflowing.
  .sales .search-input-wrap {
    flex: 1 1 10rem;
  }
  .sales .search-input-wrap .input-search {
    width: 100%;
  }
}
</style>
